import { z } from 'zod';
export const PERMISSIONS = [
    'organizations.read',
    'organizations.manage',
    'memberships.read',
    'memberships.manage',
    'roles.read',
    'roles.manage',
    'audit.read',
    'companies.read',
    'companies.manage',
    'contacts.read',
    'contacts.manage',
    'leads.read',
    'leads.manage',
    'opportunities.read',
    'opportunities.manage',
    'projects.read',
    'projects.manage',
    'tasks.read',
    'tasks.manage',
    'documents.read',
    'documents.manage',
    'notifications.read',
    'notifications.manage',
    'dashboard.read',
    'reports.read',
    /**
     * The financial reports: the Portfolio Financial Dashboard, the T-12
     * comparison, and the notes written against them. Split from `reports.read`
     * because property managers keep that one -- the occupancy page reads saved
     * views, feed status and month sign-off through it -- but must not see these.
     * Stephanie Friedl, 2026-10-22 (Forge #1113): "I would not want them to have
     * the financial dashboard or T12 comparison, or month close ... they really
     * only need the manager budget view on the reports page."
     */
    'reports.finance',
    /**
     * The manager budget view: budget against actual on the lines an
     * administrator chose, for the member's own parks. SOW deliverable 7.
     */
    'reports.budget',
    'reports.export',
    'integrations.read',
    'integrations.manage',
    // Suncoast: park and reservation reporting. 'parks.read.all' grants
    // organization-wide park visibility and is checked inside the park and
    // reservation RLS policies, not only at the API guard.
    'parks.read',
    'parks.read.all',
    'parks.manage',
    'reservations.read',
    /**
     * Marking a park's month reconciled. Write-only in spirit: reading the state
     * needs nothing beyond `reports.read`, because an owner seeing that their
     * park's month is signed off is useful and harmless.
     *
     * Granted to `administrator` alone, which happens automatically because that
     * template is [...PERMISSIONS]. It is deliberately NOT given to
     * property_manager: who signs off a month is a question about the client's
     * finance process, and guessing at it would silently hand sign-off authority
     * to whoever holds that role. Add it when they tell us to.
     */
    'reports.reconcile',
    /**
     * Writing commentary onto a park's month. Reading a note needs only
     * `reports.read`, because the whole point of the commentary is that the people
     * reading the numbers see it.
     *
     * Administrator only, for the same reason as `reports.reconcile`: the client
     * has not said who authors the monthly commentary, and a note carries the
     * author's name to everyone who can see that park. Widen it when they ask.
     */
    'reports.note',
    /**
     * A park's manager closing its month: confirming its move-ins and move-outs,
     * noting its irregular budget lines, then closing each (Stephanie Friedl,
     * 2026-10-23, Spark 287224 and 287230). For the parks the member can see.
     * Reopening a closed month needs `reports.reconcile`, so a manager cannot
     * undo their own close.
     */
    'months.close',
];
export const PermissionSchema = z.enum(PERMISSIONS);
export const ROLE_TEMPLATES = {
    administrator: [...PERMISSIONS],
    manager: [
        'organizations.read',
        'memberships.read',
        'roles.read',
        'audit.read',
        'companies.read',
        'companies.manage',
        'contacts.read',
        'contacts.manage',
        'leads.read',
        'leads.manage',
        'opportunities.read',
        'opportunities.manage',
        'projects.read',
        'projects.manage',
        'tasks.read',
        'tasks.manage',
        'documents.read',
        'documents.manage',
        'notifications.read',
        'dashboard.read',
        'reports.read',
        'reports.finance',
        'reports.budget',
        'reports.export',
        'parks.read',
        'reservations.read',
    ],
    member: [
        'organizations.read',
        'companies.read',
        'contacts.read',
        'leads.read',
        'opportunities.read',
        'projects.read',
        'tasks.read',
        'tasks.manage',
        'documents.read',
        'documents.manage',
        'notifications.read',
        'dashboard.read',
        'reports.read',
        'reports.finance',
        'reports.budget',
        'parks.read',
        'reservations.read',
    ],
    viewer: [
        'organizations.read',
        'companies.read',
        'contacts.read',
        'leads.read',
        'opportunities.read',
        'projects.read',
        'tasks.read',
        'documents.read',
        'notifications.read',
        'dashboard.read',
        'reports.read',
        'reports.finance',
        'reports.budget',
        'parks.read',
        'reservations.read',
    ],
    /**
     * Suncoast's own roles, named by Christan PousCordero on 2026-08-27: Admin,
     * Owner, Investor and Property Manager. Admin is `administrator` above.
     *
     * None of the three below carries `parks.read.all`. That is the whole point:
     * an owner sees a park because a park_access row says so, and Brian Ford owns
     * two of the five. Granting read.all to an owner role would hand him the other
     * three and no test would notice, because every query would still succeed.
     */
    owner: [
        'organizations.read',
        'dashboard.read',
        'reports.read',
        'reports.finance',
        'reports.budget',
        'reports.export',
        'parks.read',
        'reservations.read',
    ],
    /** Christan: "probably for future use". Read-only, no export. */
    investor: [
        'organizations.read',
        'dashboard.read',
        'reports.read',
        'reports.finance',
        'reports.budget',
        'parks.read',
        'reservations.read',
    ],
    /**
     * Occupancy and the manager budget view, for their own parks, and nothing
     * else: no financial reports, no exports, no member list. Stephanie Friedl,
     * 2026-10-22 (Forge #1113): "really just reservations, parks and manager
     * budget view".
     */
    property_manager: [
        'organizations.read',
        'dashboard.read',
        'reports.read',
        'reports.budget',
        'months.close',
        'parks.read',
        'reservations.read',
    ],
};
